import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C, LAYOUT } from "../constants/colors";
import { FONT } from "../theme/tokens";

/** Accent palettes — student blue / faculty green plus neutrals. */
export const FIELD_TONES = {
  navy: { accent: "#1B4F72", soft: "#E7F0F6", chip: "#143B56" },
  blue: { accent: "#2E6BE6", soft: "#EAF0FF", chip: "#173673" },
  green: { accent: "#2659BF", soft: "#EAF0FF", chip: "#173673" },
  amber: { accent: "#1B4F72", soft: "#E7F0F6", chip: "#143B56" },
  coral: { accent: C.error, soft: C.errorBg, chip: "#B81422" },
  teal: { accent: "#0F766E", soft: "#E6F7F5", chip: "#115E59" },
  slate: { accent: "#334155", soft: "#F1F5F9", chip: "#1E293B" },
  ink: { accent: "#0B0B0B", soft: "#F3F4F6", chip: "#0B0B0B" },
  orange: { accent: C.primary, soft: C.primarySoft, chip: C.primaryDark },
};

function toneOf(name = "blue") {
  return FIELD_TONES[name] || FIELD_TONES.blue;
}

const baseControl = {
  width: "100%",
  padding: "11px 12px",
  fontSize: 14,
  fontFamily: FONT.family,
  color: C.text,
  background: "#fff",
  boxSizing: "border-box",
  borderRadius: 8,
  outline: "none",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  lineHeight: 1.45,
};

function optionLabel(node) {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(optionLabel).join("");
  if (React.isValidElement(node)) return optionLabel(node.props?.children);
  return "";
}

/** Collect `<option>` children into { value, label, disabled }. */
export function parseSelectOptions(children) {
  const out = [];
  const walk = (nodes) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;
      if (child.type === React.Fragment) {
        walk(child.props.children);
        return;
      }
      if (child.type !== "option" && child.props?.value === undefined) return;
      out.push({
        value: child.props.value,
        label: optionLabel(child.props.children) || String(child.props.value ?? ""),
        disabled: !!child.props.disabled,
      });
    });
  };
  walk(children);
  return out;
}

/**
 * Labeled field shell with colored left rail + soft panel.
 */
export function FieldShell({
  label,
  hint,
  icon,
  tone = "blue",
  required = false,
  children,
  style = {},
  hintColor,
}) {
  const t = toneOf(tone);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, ...style }}>
      {(label || icon) && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            fontWeight: 600,
            color: C.text,
            letterSpacing: 0,
          }}
        >
          {icon && (
            <span
              style={{
                width: 18,
                height: 18,
                color: t.accent,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className={`ti ${icon}`} style={{ fontSize: 14 }} />
            </span>
          )}
          <span>
            {label}
            {required && (
              <span style={{ color: C.error, marginLeft: 3 }} aria-hidden>
                *
              </span>
            )}
          </span>
        </label>
      )}
      <div>{children}</div>
      {hint != null && hint !== false && (
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: hintColor || C.muted,
            lineHeight: 1.4,
            paddingLeft: 1,
            fontWeight: 400,
            minHeight: 18,
          }}
        >
          {hint || "\u00a0"}
        </p>
      )}
    </div>
  );
}

function useFocusRing(tone) {
  const [focused, setFocused] = useState(false);
  const t = toneOf(tone);
  const ring = focused
    ? {
        borderColor: t.accent,
        boxShadow: `0 0 0 3px ${t.soft}`,
        background: "#fff",
      }
    : {
        borderColor: C.borderStrong,
        boxShadow: "none",
        background: "#fff",
      };
  return {
    focused,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    ring,
    tone: t,
  };
}

export function FancyInput({
  tone = "blue",
  label,
  hint,
  icon,
  required,
  style,
  disabled,
  ...props
}) {
  const { onFocus, onBlur, ring } = useFocusRing(tone);
  const control = (
    <input
      {...props}
      disabled={disabled}
      onFocus={(e) => {
        onFocus();
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        onBlur();
        props.onBlur?.(e);
      }}
      style={{
        ...baseControl,
        border: `1px solid ${C.borderStrong}`,
        ...ring,
        opacity: disabled ? 0.65 : 1,
        cursor: disabled ? "not-allowed" : "text",
        ...style,
      }}
    />
  );
  if (!label && !hint && !icon) return control;
  return (
    <FieldShell label={label} hint={hint} icon={icon} tone={tone} required={required}>
      {control}
    </FieldShell>
  );
}

/**
 * Custom colorful select — portal menu so option rows are styled (native <select> cannot).
 * API-compatible: value, onChange(e) with e.target.value, disabled, children as <option>.
 */
export function FancySelect({
  tone = "blue",
  label,
  hint,
  icon,
  required,
  children,
  style,
  disabled,
  valueColor,
  value,
  onChange,
  onFocus,
  onBlur,
  name,
  id,
}) {
  const t = toneOf(tone);
  const options = useMemo(() => parseSelectOptions(children), [children]);
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const [activeIdx, setActiveIdx] = useState(-1);

  const selected = options.find((o) => String(o.value) === String(value ?? ""));
  const display =
    selected?.label ||
    options.find((o) => o.value === "" || o.value == null)?.label ||
    "Select…";
  const isPlaceholder = !selected || selected.value === "" || selected.value == null;

  const placeMenu = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 220);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 280 && r.top > spaceBelow;
    setMenuBox({
      left,
      width,
      top: openUp ? undefined : r.bottom + 6,
      bottom: openUp ? window.innerHeight - r.top + 6 : undefined,
      maxHeight: Math.min(320, openUp ? r.top - 16 : spaceBelow - 12),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    placeMenu();
    const onWin = () => placeMenu();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => String(o.value) === String(value ?? ""));
    setActiveIdx(idx >= 0 ? idx : 0);
  }, [open, options, value]);

  const emitChange = (nextVal) => {
    onChange?.({
      target: { value: nextVal, name, id },
      currentTarget: { value: nextVal, name, id },
    });
  };

  const pick = (opt) => {
    if (opt.disabled) return;
    emitChange(opt.value);
    setOpen(false);
    onBlur?.({ target: { value: opt.value, name, id } });
  };

  const toggle = () => {
    if (disabled) return;
    setOpen((v) => {
      const next = !v;
      if (next) onFocus?.({ target: { value, name, id } });
      else onBlur?.({ target: { value, name, id } });
      return next;
    });
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
      onFocus?.({ target: { value, name, id } });
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, (i < 0 ? -1 : i) + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
    } else if (e.key === "Enter" && activeIdx >= 0 && options[activeIdx]) {
      e.preventDefault();
      pick(options[activeIdx]);
    }
  };

  const control = (
    <div style={{ position: "relative" }}>
      <button
        ref={btnRef}
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKeyDown}
        style={{
          ...baseControl,
          paddingRight: 42,
        border: `1px solid ${open ? t.accent : C.borderStrong}`,
          boxShadow: open ? `0 0 0 3px ${t.soft}` : "none",
          background: "#fff",
          fontWeight: 500,
          color: valueColor || (disabled || isPlaceholder ? C.faint : C.text),
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.7 : 1,
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          ...style,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {display}
        </span>
        <span
          aria-hidden
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            width: 24,
            height: 24,
            borderRadius: 7,
            background: t.soft,
            color: t.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
          }}
        >
          <i className={`ti ti-chevron-${open ? "up" : "down"}`} style={{ fontSize: 14, fontWeight: 700 }} />
        </span>
      </button>

      {open &&
        menuBox &&
        createPortal(
          <>
            <div
              style={{ position: "fixed", inset: 0, zIndex: 1400 }}
              onClick={() => {
                setOpen(false);
                onBlur?.({ target: { value, name, id } });
              }}
            />
            <div
              role="listbox"
              style={{
                position: "fixed",
                zIndex: 1401,
                left: menuBox.left,
                width: menuBox.width,
                top: menuBox.top,
                bottom: menuBox.bottom,
                maxHeight: menuBox.maxHeight,
                background: "#fff",
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  background: "#F8FAFC",
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.muted,
                    letterSpacing: 0.02,
                  }}
                >
                  {label || "Choose an option"}
                </span>
              </div>
              <div style={{ overflowY: "auto", padding: 6, flex: 1 }}>
                {options.map((opt, idx) => {
                  const isSel = String(opt.value) === String(value ?? "");
                  const isActive = idx === activeIdx;
                  return (
                    <button
                      key={`${String(opt.value)}-${idx}`}
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      disabled={opt.disabled}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => pick(opt)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 12px",
                        marginBottom: 2,
                        border: "none",
                        borderRadius: 6,
                        cursor: opt.disabled ? "not-allowed" : "pointer",
                        opacity: opt.disabled ? 0.45 : 1,
                        background: isSel ? t.soft : isActive ? "#F8FAFC" : "transparent",
                        color: isSel ? t.chip : C.text,
                        fontWeight: isSel ? 600 : 400,
                        fontSize: 14,
                        boxShadow: "none",
                        transition: "background 0.12s ease",
                      }}
                    >
                      <span style={{ flex: 1, lineHeight: 1.35 }}>{opt.label}</span>
                      {isSel && (
                        <i
                          className="ti ti-check"
                          style={{ fontSize: 16, color: t.accent, flexShrink: 0 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );

  if (!label && !hint && !icon) return control;
  return (
    <FieldShell label={label} hint={hint} icon={icon} tone={tone} required={required}>
      {control}
    </FieldShell>
  );
}

export function FancyTextarea({
  tone = "blue",
  label,
  hint,
  icon,
  required,
  style,
  ...props
}) {
  const { onFocus, onBlur, ring } = useFocusRing(tone);
  const control = (
    <textarea
      {...props}
      onFocus={(e) => {
        onFocus();
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        onBlur();
        props.onBlur?.(e);
      }}
      style={{
        ...baseControl,
        minHeight: 88,
        resize: "vertical",
        border: `1px solid ${C.borderStrong}`,
        ...ring,
        ...style,
      }}
    />
  );
  if (!label && !hint && !icon) return control;
  return (
    <FieldShell label={label} hint={hint} icon={icon} tone={tone} required={required}>
      {control}
    </FieldShell>
  );
}

/** Plain style object for third-party pickers (DreamCompanyPicker). */
export function fancyControlStyle(tone = "blue", focused = false) {
  const t = toneOf(tone);
  return {
    ...baseControl,
    border: `1px solid ${focused ? t.accent : C.borderStrong}`,
    boxShadow: focused ? `0 0 0 3px ${t.soft}` : "none",
    background: "#fff",
    borderRadius: LAYOUT.radiusSm,
  };
}

/** Soft panel wrapping a group of related fields. */
export function FieldGroup({ title, icon, tone = "blue", children, style = {} }) {
  const t = toneOf(tone);
  return (
    <div
      style={{
        marginTop: 14,
        padding: "14px 14px 12px",
        borderRadius: 10,
        background: "#F8FAFC",
        border: `1px solid ${C.border}`,
        boxShadow: "none",
        ...style,
      }}
    >
      {(title || icon) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            paddingBottom: 8,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          {icon && (
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "#fff",
                border: `1px solid ${t.accent}44`,
                color: t.accent,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className={`ti ${icon}`} style={{ fontSize: 15 }} />
            </span>
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text, letterSpacing: 0 }}>
            {title}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

/** Pace chip colors for Slow / Steady / Average / Fast. */
export function paceTone(skillLevel = 3) {
  const s = Number(skillLevel) || 3;
  if (s <= 1) return "amber";
  if (s === 2) return "amber";
  if (s >= 4) return "green";
  return "blue";
}

/** Parse stored "HH:MM" (24h) or display "9:00 AM" → parts, or null */
export function parseTimeParts(raw) {
  const s = String(raw || "")
    .trim()
    .replace(/\./g, ":")
    .replace(/\s+/g, " ");
  if (!s) return null;

  const withAp = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (withAp) {
    let h = parseInt(withAp[1], 10);
    const min = parseInt(withAp[2], 10);
    const apIn = withAp[3].toUpperCase();
    if (h < 1 || h > 12 || min > 59) return null;
    if (apIn === "PM" && h < 12) h += 12;
    if (apIn === "AM" && h === 12) h = 0;
    const ap = h >= 12 ? "PM" : "AM";
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return { h12, m: min, ap, totalMin: h * 60 + min };
  }

  // Stored 24h "HH:MM" only (no AM/PM) — used by backend / localStorage
  const hhmm = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!hhmm) return null;
  const h = parseInt(hhmm[1], 10);
  const min = parseInt(hhmm[2], 10);
  if (h > 23 || min > 59) return null;
  const ap = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return { h12, m: min, ap, totalMin: h * 60 + min };
}

/**
 * Strict user typing only: must be "h:mm AM|PM" (minutes required, AM/PM required).
 * Rejects "9", "9:00", "25:00 AM", "9:99 AM", random text.
 */
export function parseTimeInputStrict(raw) {
  const s = String(raw || "")
    .trim()
    .replace(/\./g, ":")
    .replace(/\s+/g, " ")
    .toUpperCase();
  if (!s) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 1 || h > 12 || min < 0 || min > 59) return null;
  const apIn = m[3];
  if (apIn === "PM" && h < 12) h += 12;
  if (apIn === "AM" && h === 12) h = 0;
  const ap = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return { h12, m: min, ap, totalMin: h * 60 + min };
}

/** Format stored "HH:MM" (24h) as "9:00 AM" */
export function formatTime12(raw) {
  const p = parseTimeParts(raw);
  if (!p) return "";
  return `${p.h12}:${String(p.m).padStart(2, "0")} ${p.ap}`;
}

/** Build 24h "HH:MM" from 12h parts (for storage / backend) */
export function toTime24(h12, m, ap) {
  let h = Number(h12) || 12;
  const min = Math.min(59, Math.max(0, Number(m) || 0));
  const a = String(ap || "AM").toUpperCase() === "PM" ? "PM" : "AM";
  if (a === "AM") {
    if (h === 12) h = 0;
  } else if (h < 12) {
    h += 12;
  }
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/** Minutes from midnight; null if invalid/empty */
export function timeToMinutes(raw) {
  const p = parseTimeParts(raw);
  return p ? p.totalMin : null;
}

/** Add minutes to a stored 24h "HH:MM" time. */
export function addMinutesToTime(raw, delta) {
  const t = timeToMinutes(raw);
  if (t == null || !Number.isFinite(Number(delta))) return "";
  let n = t + Number(delta);
  if (n < 0) n = 0;
  if (n > 23 * 60 + 59) n = 23 * 60 + 59;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Minutes between two stored times; 0 if invalid. */
export function minutesBetween(startRaw, endRaw) {
  const a = timeToMinutes(startRaw);
  const b = timeToMinutes(endRaw);
  if (a == null || b == null || b <= a) return 0;
  return b - a;
}

/** Format duration between two stored times */
export function formatDurationLabel(startRaw, endRaw) {
  const a = timeToMinutes(startRaw);
  const b = timeToMinutes(endRaw);
  if (a == null || b == null || b <= a) return "";
  const total = b - a;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return h === 1 ? "1 hr" : `${h} hr`;
  return `${h} hr ${m} min`;
}

/**
 * Validate a full day timing set. Returns { ok, errors[], warnings[] }.
 */
export function validateDayTiming({
  numDays,
  startTime,
  endTime,
  lunchStart = "",
  lunchEnd = "",
  breaks = [],
} = {}) {
  const errors = [];
  const warnings = [];
  const days = Number(numDays);
  if (!Number.isFinite(days) || days < 1 || days > 365) {
    errors.push("Plan length must be between 1 and 365 days.");
  }

  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (!String(startTime || "").trim() || start == null) {
    errors.push("Day start must be a valid time like 9:00 AM.");
  }
  if (!String(endTime || "").trim() || end == null) {
    errors.push("Day end must be a valid time like 5:00 PM.");
  }
  if (start != null && end != null) {
    if (end <= start) errors.push("Day end must be after day start.");
    else {
      const span = end - start;
      if (span < 3 * 60) errors.push("Class day must be at least 3 hours.");
      else if (span < 4 * 60) warnings.push("Class day is under 4 hours — slots will be tight.");
      if (span > 12 * 60) errors.push("Class day cannot exceed 12 hours.");
    }
  }

  const ls = String(lunchStart || "").trim();
  const le = String(lunchEnd || "").trim();
  if (ls || le) {
    const lunchS = timeToMinutes(ls);
    const lunchE = timeToMinutes(le);
    if (!ls || lunchS == null) errors.push("Pick when lunch starts (example: 12:30 PM).");
    if (!le || lunchE == null) errors.push("Pick when lunch ends.");
    if (lunchS != null && lunchE != null) {
      if (lunchE <= lunchS) errors.push("Lunch must end after it starts.");
      if (start != null && end != null && (lunchS < start || lunchE > end)) {
        errors.push("Lunch must be during class hours.");
      }
    }
  }

  (breaks || []).forEach((b, i) => {
    const bs = String(b?.start || "").trim();
    const be = String(b?.end || "").trim();
    if (!bs && !be) return;
    const bStart = timeToMinutes(bs);
    const bEnd = timeToMinutes(be);
    if (!bs || bStart == null) errors.push(`Break ${i + 1} start must be like 10:45 AM.`);
    if (!be || bEnd == null) errors.push(`Break ${i + 1} end must be like 11:00 AM.`);
    if (bStart != null && bEnd != null) {
      if (bEnd <= bStart) errors.push(`Break ${i + 1} end must be after its start.`);
      if (start != null && end != null && (bStart < start || bEnd > end)) {
        errors.push(`Break ${i + 1} must sit inside the class day.`);
      }
      if (ls && le) {
        const lunchS = timeToMinutes(ls);
        const lunchE = timeToMinutes(le);
        if (lunchS != null && lunchE != null && bStart < lunchE && bEnd > lunchS) {
          errors.push(`Break ${i + 1} overlaps lunch.`);
        }
      }
    }
  });

  const filled = (breaks || [])
    .map((b, i) => ({ i, s: timeToMinutes(b?.start), e: timeToMinutes(b?.end) }))
    .filter((x) => x.s != null && x.e != null && x.e > x.s);
  for (let a = 0; a < filled.length; a++) {
    for (let b = a + 1; b < filled.length; b++) {
      if (filled[a].s < filled[b].e && filled[a].e > filled[b].s) {
        errors.push(`Break ${filled[a].i + 1} overlaps Break ${filled[b].i + 1}.`);
      }
    }
  }

  return { ok: errors.length === 0, errors, warnings };
}

/**
 * Familiar clock picker (native time). Stores 24h "HH:MM".
 * Shows a 12-hour readout so AM/PM is obvious without a fragile text box.
 */
export function NativeTimeField({
  tone = "blue",
  label,
  hint,
  icon,
  required,
  value = "",
  onChange,
  disabled = false,
  allowEmpty = false,
  error: externalError = "",
  style = {},
}) {
  const t = toneOf(tone);
  const { focused, onFocus, onBlur, ring } = useFocusRing(tone);
  const stored = String(value || "").trim();
  const pretty = stored ? formatTime12(stored) : "";
  const err = externalError;

  return (
    <FieldShell
      label={label}
      icon={icon}
      tone={tone}
      required={required}
      hint={err || hint || pretty || undefined}
      hintColor={err ? C.error : undefined}
      style={{ width: "100%", minWidth: 0, ...style }}
    >
      <input
        type="time"
        step={60}
        disabled={disabled}
        value={stored}
        required={required && !allowEmpty}
        aria-label={label || "Time"}
        aria-invalid={err ? true : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => {
          const next = e.target.value || "";
          if (!next && !allowEmpty) return;
          onChange?.({ target: { value: next } });
        }}
        style={{
          ...baseControl,
          width: "100%",
          border: `1px solid ${err ? C.error : C.borderStrong}`,
          ...(!err
            ? ring
            : { borderColor: C.error, boxShadow: `0 0 0 3px ${C.errorBg}`, background: "#fff" }),
          opacity: disabled ? 0.65 : 1,
          fontVariantNumeric: "tabular-nums",
          accentColor: t.accent,
        }}
      />
    </FieldShell>
  );
}

/**
 * Time field: type h:mm + AM/PM toggle in one control.
 * Invalid input is rejected and reverted. value is 24h "HH:MM".
 */
export function FancyTime12({
  tone = "blue",
  label,
  hint,
  icon,
  required,
  value = "",
  onChange,
  disabled = false,
  allowEmpty = false,
  placeholder = "9:00",
  error: externalError = "",
  style = {},
}) {
  const t = toneOf(tone);
  const { focused, onFocus, onBlur, ring } = useFocusRing(tone);
  const parts = parseTimeParts(value);
  const [clock, setClock] = useState(() =>
    parts ? `${parts.h12}:${String(parts.m).padStart(2, "0")}` : ""
  );
  const [ap, setAp] = useState(parts?.ap || "AM");
  const [localError, setLocalError] = useState("");
  const focusedRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    const p = parseTimeParts(value);
    if (!p) {
      if (!value) {
        setClock("");
        if (!externalError) setLocalError("");
      }
      return;
    }
    setClock(`${p.h12}:${String(p.m).padStart(2, "0")}`);
    setAp(p.ap);
    if (!externalError) setLocalError("");
  }, [value, externalError]);

  const err = externalError || localError;

  const revert = () => {
    const p = parseTimeParts(value);
    setClock(p ? `${p.h12}:${String(p.m).padStart(2, "0")}` : "");
    if (p?.ap) setAp(p.ap);
  };

  const commit = (clockText, period) => {
    const trimmed = String(clockText || "").trim();
    if (!trimmed) {
      if (allowEmpty) {
        setLocalError("");
        setClock("");
        onChange?.({ target: { value: "" } });
        return true;
      }
      setLocalError("Enter a time, then choose AM or PM");
      revert();
      return false;
    }

    // Accept full "9:00 AM" pasted into the clock box, or "9:00" + toggle
    let p =
      parseTimeInputStrict(trimmed) ||
      parseTimeInputStrict(`${trimmed} ${period}`);

    if (!p) {
      // Soft-normalize missing colon: 900 → try 9:00 with period
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length === 3 || digits.length === 4) {
        const normalized =
          digits.length === 3
            ? `${digits[0]}:${digits.slice(1)} ${period}`
            : `${digits.slice(0, 2)}:${digits.slice(2)} ${period}`;
        p = parseTimeInputStrict(normalized);
      }
    }

    if (!p) {
      setLocalError("Use h:mm — e.g. 9:00 — then AM or PM");
      revert();
      return false;
    }

    const stored = toTime24(p.h12, p.m, p.ap);
    setLocalError("");
    setClock(`${p.h12}:${String(p.m).padStart(2, "0")}`);
    setAp(p.ap);
    onChange?.({ target: { value: stored } });
    return true;
  };

  const onClockChange = (raw) => {
    // Allow digits, colon, and optional trailing AM/PM while typing/pasting
    let next = String(raw).replace(/[^\d:apmAPM\s]/g, "");
    const onlyDigits = next.replace(/[^\d]/g, "");
    if (/^\d{3,4}$/.test(onlyDigits) && !next.includes(":")) {
      if (onlyDigits.length === 3) next = `${onlyDigits[0]}:${onlyDigits.slice(1)}`;
      if (onlyDigits.length === 4) next = `${onlyDigits.slice(0, 2)}:${onlyDigits.slice(2)}`;
    }
    // If user typed AM/PM into the box, sync the toggle
    const apMatch = next.match(/\b(AM|PM)\b/i);
    if (apMatch) setAp(apMatch[1].toUpperCase());
    setClock(next.replace(/\s*(AM|PM)\s*/i, "").trim());
    if (localError) setLocalError("");
  };

  const setPeriod = (nextAp) => {
    if (disabled) return;
    setAp(nextAp);
    if (String(clock || "").trim()) {
      commit(clock.replace(/\s*(AM|PM)\s*/i, "").trim(), nextAp);
    }
  };

  const control = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        ...baseControl,
        width: "100%",
        padding: "5px 5px 5px 12px",
        border: `1px solid ${err ? C.error : C.borderStrong}`,
        ...(!err
          ? ring
          : { borderColor: C.error, boxShadow: `0 0 0 3px ${C.errorBg}`, background: "#fff" }),
        opacity: disabled ? 0.65 : 1,
      }}
    >
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={err ? true : undefined}
        aria-label={`${label || "Time"} hour and minutes`}
        value={clock}
        onFocus={(e) => {
          focusedRef.current = true;
          onFocus();
          e.target.select();
        }}
        onChange={(e) => onClockChange(e.target.value)}
        onBlur={() => {
          focusedRef.current = false;
          onBlur();
          commit(clock, ap);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
          if (e.key === "a" || e.key === "A") {
            e.preventDefault();
            setPeriod("AM");
          }
          if (e.key === "p" || e.key === "P") {
            e.preventDefault();
            setPeriod("PM");
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: 13.5,
          fontWeight: 700,
          fontFamily: FONT.family,
          color: C.text,
          padding: "7px 0",
          letterSpacing: "0.01em",
        }}
      />
      <div
        role="group"
        aria-label="AM or PM"
        style={{
          display: "inline-flex",
          padding: 2,
          borderRadius: 8,
          background: focused ? `${t.accent}14` : "#EEF2F7",
          flexShrink: 0,
        }}
      >
        {["AM", "PM"].map((x) => {
          const on = ap === x;
          return (
            <button
              key={x}
              type="button"
              disabled={disabled}
              aria-pressed={on}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setPeriod(x)}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.03em",
                cursor: disabled ? "not-allowed" : "pointer",
                background: on ? t.accent : "transparent",
                color: on ? "#fff" : C.muted,
                boxShadow: on ? `0 1px 3px ${t.accent}44` : "none",
                transition: "background 0.12s ease, color 0.12s ease",
              }}
            >
              {x}
            </button>
          );
        })}
      </div>
    </div>
  );

  const help = err || hint || undefined;

  return (
    <FieldShell
      label={label}
      icon={icon}
      tone={tone}
      required={required}
      hint={help}
      hintColor={err ? C.error : undefined}
      style={{ width: "100%", minWidth: 0, ...style }}
    >
      {control}
    </FieldShell>
  );
}

export { baseControl as fancyBaseControl };
